<script>
import { map, range } from 'lodash-es';
import SkiDatePicker from './SkiDatePicker';
import i18n from '../../../i18n';

export default {
  name: 'SkiPricePeriods',
  components: {
    SkiDatePicker,
  },
  props: {
    period: {
      type: Object,
      required: true,
    },
    season: {
      type: Object,
      required: true,
    },
    personCategories: {
      type: Array,
      required: true,
    },
    periodLoading: {
      type: Boolean,
      required: true,
    },
    savePeriodLoading: {
      type: Boolean,
      required: true,
    },
    savePeriodError: {
      type: String,
      default: '',
    },
    pricesLoading: {
      type: Boolean,
      default: false,
    },
    savePriceLoading: {
      type: Boolean,
      required: true,
    },
    savePriceError: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      periodItem: Object.assign({}, this.period),
      selectedDay: undefined,
      selectedDays: [],
      dataHeaders: [
        {
          text: i18n.t('ski.skiPrice.dataHeaders.name'),
          value: 'personCategoryName',
          width: '200px',
        },
        {
          text: i18n.t('ski.skiPrice.dataHeaders.grossPrice'),
          value: 'grossPrice',
          isUsedForGross: true,
          width: '100px',
        },
        {
          text: i18n.t('ski.skiPrice.dataHeaders.commission'),
          value: 'commission',
          isUsedForGross: true,
          width: '100px',
        },
        {
          text: i18n.t('ski.skiPrice.dataHeaders.netPrice'),
          value: 'netPrice',
          isUsedForGross: false,
          width: '100px',
        },
        {
          text: i18n.t('ski.skiPrice.dataHeaders.contribution'),
          value: 'contribution',
          isUsedForGross: false,
          width: '100px',
        },
        {
          text: i18n.t('ski.skiPrice.dataHeaders.keyCardRequired'),
          value: 'keyCardRequired',
          width: '100px',
        },
        {
          text: '',
          value: '',
          width: '100px',
        },
      ],
      pagination: {
        rowsPerPage: -1,
        descending: false,
        sortBy: 'personCategoryName',
      },
      toggled: false,
    };
  },
  watch: {
    period: {
      deep: true,
      handler() {
        this.periodItem = Object.assign({}, this.period);
      },
    },
  },
  methods: {
    availableDays() {
      return range(1, 29);
    },
    updateSelectedDay(value) {
      this.selectedDay = value;
    },
    allowedDates(date) {
      return date > this.periodItem.arrivalFrom;
    },
    mapDayPersonCategories(day) {
      if (Object.values(day.personCategories).length === 0) {
        return [];
      }
      return map(day.personCategories, personCategory => ({
        id: personCategory.id,
        skiPassPersonCategoryId: personCategory.skiPassPersonCategoryId,
        days: personCategory.days,
        price: personCategory.price,
        commissionRatio: personCategory.commissionRatio,
        contributionRatio: personCategory.contributionRatio,
        name: personCategory.name,
        externalName: personCategory.externalName,
        ageFrom: personCategory.ageFrom,
        ageTo: personCategory.ageTo,
        requireKeyCard: personCategory.requireKeyCard,
      }));
    },
    getPriceButtonSaveOrCreateText(priceId) {
      return priceId ? this.$t('buttons.save') : this.$t('buttons.create');
    },
  },
  computed: {
    getPeriodHeader() {
      return this.period.id
        ? this.$t('ski.skiPricePeriod.title', {
          periodId: this.period.id,
        })
        : this.$t('ski.skiPricePeriods.new');
    },
    getPeriodButtonSaveOrCreateText() {
      return this.period.id ? this.$t('buttons.save') : this.$t('buttons.create');
    },
  },
};
</script>

<template>
  <v-container class="pa-0">
    <v-layout xs12>
      <v-flex sm2 xs12>
        <h3
          :class="{'red--text': !period.id}"
          class="d-inline-block pr-2 pt-4"
        >
          {{ getPeriodHeader }}
          <span v-if="period.periodPairedWithPeriodId">
          <v-icon
            :title="'paired with #' + period.periodPairedWithPeriodId"
            class="grey--text"
          >
            info
          </v-icon>
        </span>
        </h3>
        <v-alert
          :value="this.savePeriodError"
          color="error"
          icon="warning"
          outline
        >
          {{ this.savePeriodError }}
        </v-alert>
      </v-flex>
      <v-flex sm10 xs12>
        <v-layout v-for="dates in period.dates" :key="dates.id" xs12>
          <v-flex sm2 xs12>
            <ski-date-picker
              :date.sync="dates.arrivalFrom"
              :disabled="savePeriodLoading"
              :title="$t('ski.datePicker.from')"
              @onChange="$emit('onChange', {
                datesItem: dates,
                periodId: dates.id,
                params: { arrivalFrom: $event }
              })"
            />
          </v-flex>
          <v-flex sm2 xs12>
            <ski-date-picker
              :date.sync="dates.arrivalTo"
              :disabled="dates.arrivalFrom === undefined || savePeriodLoading"
              :allowed-dates="allowedDates"
              :title="$t('ski.datePicker.to')"
              @onChange="$emit('onChange', {
                datesItem: dates,
                periodId: dates.id,
                params: { arrivalTo: $event }
              })"
            />
          </v-flex>
          <v-flex sm8 xs12>
            <v-btn
              :disabled="savePeriodLoading"
              class="ml-2 mt-3"
              color="success"
              small
              outline
              @click="$emit('createOrUpdatePeriod', dates)"
            >
              {{ getPeriodButtonSaveOrCreateText }}
            </v-btn>
            <v-btn
              :disabled="savePeriodLoading"
              class="ml-2 mt-3"
              color="error"
              small
              outline
              @click="$emit('removePeriodByPeriodId', dates.id)"
            >
              {{ $t('ski.skiPricePeriod.remove') }}
            </v-btn>
            <v-btn
              v-if="period.id && !period.periodPairedWithPeriodId && period.id === dates.id"
              :disabled="savePeriodLoading || !Object.keys(period.days).length"
              class="ml-2 mt-3"
              color="success"
              small
              outline
              @click="$emit('createPeriodToAnotherPeriod', dates)"
            >
              {{ $t('ski.skiPricePeriod.addAnotherPeriod') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout v-if="!period.periodPairedWithPeriodId && period.id">
      <v-flex sm10 xs12 offset-sm2 mb-2>
        <h3 class="d-inline-block">{{ $t('ski.skiPricePeriod.subtitle') }}</h3>
        <v-flex class="d-inline-block pl-3" sm2 xs12>
          <v-select
            :items="availableDays()"
            :disabled="period.id === undefined || savePeriodLoading || savePriceLoading"
            :label="$t('ski.skiPricePeriod.days')"
            @change="updateSelectedDay"
          />
        </v-flex>
        <v-btn
          :disabled="this.selectedDays.includes(this.selectedDay) || this.selectedDay == undefined"
          class="mb-2"
          color="success"
          small
          outline
          @click="$emit('addNewPrice', { periodId: period.id, selectedDay }); toggled = true"
        >
          {{ $t('ski.skiPricePeriod.addDay') }}
        </v-btn>
        <v-btn
          v-if="period.id"
          :loading="periodLoading || savePeriodLoading || pricesLoading || savePriceLoading"
          icon
          @click="$emit('loadPricesByPeriodId', period.id)"
        >
          <v-icon>cached</v-icon>
        </v-btn>
        <v-btn
          :disabled="!Object.keys(period.days).length"
          color="primary"
          class="ml-auto"
          @click="toggled = !toggled"
        >
          {{ toggled ? 'Hide' : 'Show' }}
        </v-btn>
      </v-flex>
    </v-layout>
    <template v-if="toggled">
      <v-layout
        v-for="(day, numberOfDays) in periodItem.days"
        :key="'ski-price-period-' + numberOfDays"
        xs12
      >
        <v-layout wrap>
          <div class="days-title">
            <h2 class="font-weight-bold">
              {{ $t('ski.skiInput.title', { days: numberOfDays }) }}
            </h2>
            <v-btn
              :disabled="savePriceLoading"
              color="error"
              medium
              @click="$emit('removePricesInDay', period.id, day, numberOfDays)"
            >
              {{ $t('ski.skiInput.remove') }}
            </v-btn>
          </div>
          <v-divider class="pt-3" xs12/>
          <v-flex class="pr-12" xs12>
            <v-data-table
              :headers="dataHeaders"
              :items="mapDayPersonCategories(day)"
              :pagination.sync="pagination"
              :loading="periodLoading || pricesLoading"
              hide-actions
              must-sort
              fill-height
            >
              <template slot="headers" slot-scope="props">
                <th
                  v-show="header.isUsedForGross === undefined
                    || season.netOrGross === 'gross' && header.isUsedForGross
                    || season.netOrGross === 'net' && !header.isUsedForGross"
                  v-for="header in props.headers"
                  :key="header.text"
                  :style="'width:' + header.width"
                  class="column text-xs-left"
                >
                <span class="font-size-10">
                  {{ header.text }}
                </span>
                </th>
              </template>
              <template v-slot:items="props">
                <tr>
                  <td class="font-weight-medium">
                    {{ props.item.name }}
                  </td>
                  <td v-if="season.netOrGross === 'gross'" class="font-weight-medium">
                    <v-text-field
                      @input="$emit('onPriceChange', {
                          priceItem: props.item,
                          periodId: period.id,
                          params: { price: $event }
                        })"
                      @keyup.enter.native="$emit('createOrUpdatePrice', period.id, props.item)"
                      :value="props.item.price"
                      placeholder="0.00"
                      class="width-100"
                      type="number"
                      single-line
                      solo
                      hide-details
                    />
                  </td>
                  <td v-if="season.netOrGross === 'gross'">
                    <v-text-field
                      @input="$emit('onPriceChange', {
                          priceItem: props.item,
                          periodId: period.id,
                          params: { commissionRatio: $event }
                        })"
                      @keyup.enter.native="$emit('createOrUpdatePrice', period.id, props.item)"
                      :value="props.item.commissionRatio !== undefined
                        ? props.item.commissionRatio
                        : undefined"
                      placeholder="0.00"
                      class="width-100"
                      type="number"
                      min="0"
                      suffix="%"
                      single-line
                      solo
                      hide-details
                    />
                  </td>
                  <td v-if="season.netOrGross === 'net'">
                    <v-text-field
                      @input="$emit('onPriceChange', {
                          priceItem: props.item,
                          periodId: period.id,
                          params: { price: $event }
                        })"
                      @keyup.enter.native="$emit('createOrUpdatePrice', period.id, props.item)"
                      :value="props.item.price"
                      placeholder="0.00"
                      class="width-100"
                      type="number"
                      single-line
                      solo
                      hide-details
                    />
                  </td>
                  <td v-if="season.netOrGross === 'net'">
                    <v-text-field
                      @input="$emit('onPriceChange', {
                          priceItem: props.item,
                          periodId: period.id,
                          params: { contributionRatio: $event }
                        })"
                      @keyup.enter.native="$emit('createOrUpdatePrice', period.id, props.item)"
                      :value="props.item.contributionRatio !== undefined
                        ? props.item.contributionRatio
                        : undefined"
                      placeholder="0.00"
                      min="0"
                      suffix="%"
                      class="width-100"
                      type="number"
                      single-line
                      solo
                      hide-details
                    />
                  </td>
                  <td>
                    <v-checkbox
                      @change="$emit('onPriceChange', {
                        priceItem: props.item,
                        periodId: period.id,
                        params: { requireKeyCard: $event }
                      })"
                      :input-value="props.item.requireKeyCard"
                    />
                  </td>
                  <td>
                    <v-btn
                      @click="$emit('createOrUpdatePrice', period.id, props.item)"
                      :disabled="props.item.price === undefined || savePriceLoading"
                      :color="props.item.id ? 'success' : 'warning'"
                      :class="{'text-bold': props.item.id }"
                      class="ml-0 mt-0"
                      small
                      outline
                    >
                      {{ getPriceButtonSaveOrCreateText(props.item.id) }}
                    </v-btn>
                    <v-btn
                      @click="$emit('removePriceByPriceId', period.id, props.item.id)"
                      :disabled="props.item.price === undefined || savePriceLoading"
                      class="ml-0 mt-0"
                      color="error"
                      small
                      outline
                    >
                      {{ $t('buttons.remove') }}
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-divider class="pt-3" />
          </v-flex>
        </v-layout>
      </v-layout>
    </template>
    <v-divider class="pt-3" />
  </v-container>
</template>

<style lang="scss">
  .width-100 {
    width: 100px;
  }

  .days-title {
    width: 100%;
    display: flex;
    align-items: center;
    margin: 20px 0 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/ski/skipass/SkiPricePeriod.vue